/**
 * QUIL — PERSONALISATION RUNTIME
 *
 * Applies the v3 "Colours" user-tunable knobs to the live document:
 *   • theme      → data-theme / data-scheme on <html> (light / dark / oled), through the
 *                  canonical commitTheme (app-platform theme contract)
 *   • accent     → the GROUND: the picked colour solved into --theme-bg / -surface-1 /
 *                  -surface-2 / -sunk (and the light ink and hairline) by the shared
 *                  solveGround with quil's recipe (DESIGN.md §3, "the accent is the ground")
 *   • fonts      → --f-display / --f-heading / --f-reading / --f-ui / --f-label (a OneDrive
 *                  font is picked like any other: its stack is stored in `fonts`)
 *
 * Settings live in Dexie (AppSettings). App.tsx liveQueries them and calls
 * applyPersonalization on every change; Settings/Dashboard write them back.
 */

import type { AppSettings, FontRole, Theme } from './db'
import { DEFAULT_ACCENT, DEFAULT_THEME } from './db'
import { solveGround, QUIL_HUE_RECIPE } from './themeGround'
import { commitTheme } from './themeApply'
import { readMirror, resolveMode } from './themeMode'
import type { ThemeMode, ThemePref } from './themeMode'
import { activeCustomTheme, QUIL_THEME_KEY, themeStorage } from './customThemes'

// ── Ground presets (the swatch row, DESIGN.md §3) ──
// These are GROUNDS, not accents: the writer picks the colour the whole app sits
// on and the lit element stays gold. Each is run through solveGround, which solves
// the ladder and clamps it against cream ink and the gold before any of it reaches
// a token, so these five are a starting point, not a safe-list: any colour is allowed.
export const ACCENT_PRESETS: Array<{ value: string; name: string }> = [
  { value: '#093f24', name: 'Forest' },
  { value: '#4e0603', name: 'Oxblood' },
  { value: '#1b3636', name: 'Teal' },
  { value: '#111740', name: 'Indigo' },
  { value: '#213029', name: 'Ink' },
]

export { DEFAULT_ACCENT, DEFAULT_THEME }   // re-exported from db (the seed-default source of truth)

export const THEMES: Array<{ value: Theme; label: string; hint?: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'oled', label: 'OLED', hint: 'Pure black for OLED screens' },
  { value: 'auto', label: 'Auto', hint: 'Follow the system: light by day, your dark variant by night' },
]

/** True when the OS is currently in dark mode (false in environments without matchMedia). */
function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
}

// ── Per-role font options. Value is a full CSS font-family stack; '' = token default. ──
// The reading role additionally accepts a custom OneDrive font (handled separately).
export const FONT_ROLE_LABELS: Record<FontRole, string> = {
  display: 'Display',
  heading: 'Heading',
  read: 'Reading',
  ui: 'Interface',
  label: 'Labels',
}

// Fraunces is gone with the Bright system — DESIGN.md: "No display serif. Nothing in
// this reference supports a characterful high-contrast heading face."
const SERIF_OPTIONS = [
  { label: 'Literata', value: "'Literata',Georgia,serif" },
  { label: 'Source Serif 4', value: "'Source Serif 4',Georgia,serif" },
  { label: 'Georgia', value: 'Georgia,serif' },
]
const SANS_OPTIONS = [
  { label: 'Zen Kaku Gothic New', value: "'Zen Kaku Gothic New',system-ui,sans-serif" },
  { label: 'Work Sans', value: "'Work Sans',system-ui,sans-serif" },
  { label: 'System', value: 'system-ui,sans-serif' },
]

// Bookerly is deliberately NOT listed here. It was bundled via an unofficial CDN
// copy of a Kindle-only font and was removed in the portfolio push; the licensed
// route is to drop the file into OneDrive /Apps/Quil/fonts/, where
// syncOneDriveFonts registers it and it appears in this picker automatically
// alongside these built-ins. See src/lib/customFonts.ts and src/styles/fonts.css.
// Newsreader is the Reading default, so it is the picker's "(default)" entry, not listed again.
const READING_OPTIONS = [
  { label: 'Literata', value: "'Literata',Georgia,serif" },
  { label: 'Source Serif 4', value: "'Source Serif 4',Georgia,serif" },
  { label: 'Georgia', value: 'Georgia,serif' },
]

export const FONT_OPTIONS: Record<FontRole, Array<{ label: string; value: string }>> = {
  display: SERIF_OPTIONS,
  heading: SERIF_OPTIONS,
  read: READING_OPTIONS,
  ui: SANS_OPTIONS,
  label: SANS_OPTIONS,
}

// The token defaults, mirrored so Settings can name and preview the default face and give a
// OneDrive font a fallback stack (a literal: `'Fam',var(--f-reading)` on --f-reading itself would be
// a cycle). tokens.quil.css is the source; personalization.test.ts fails if this drifts from it.
export const FONT_ROLE_DEFAULTS: Record<FontRole, string> = {
  display: "'Jagot','Archivo Narrow','Arial Narrow',system-ui,sans-serif",
  heading: "'Archivo',system-ui,sans-serif",
  read: "'Newsreader',Georgia,serif",
  ui: "'Archivo',system-ui,-apple-system,sans-serif",
  label: "'Archivo Narrow','Archivo',system-ui,sans-serif",
}

const FONT_ROLES: FontRole[] = ['display', 'heading', 'read', 'ui', 'label']

/**
 * The settings key for a role is not always the CSS token name. `read` is stored
 * as 'read' but the token is `--f-reading` (renamed in the v2 contract), so a
 * template literal here would silently write a variable nothing reads.
 */
const FONT_ROLE_VARS: Record<FontRole, string> = {
  display: '--f-display',
  heading: '--f-heading',
  read: '--f-reading',
  ui: '--f-ui',
  label: '--f-label',
}

/** Pull a human family name out of a CSS font-family stack ("'Fraunces',Georgia,serif" → "Fraunces"). */
function familyLabelFromStack(stack: string): string {
  const m = stack.match(/^\s*'([^']+)'/) ?? stack.match(/^\s*"([^"]+)"/) ?? stack.match(/^\s*([^,]+)/)
  return (m?.[1] ?? stack).trim()
}

/** The friendly name of a role's default font, so the picker can say "Fraunces" not "Default". */
export function defaultFontLabel(role: FontRole): string {
  return familyLabelFromStack(FONT_ROLE_DEFAULTS[role])
}

/**
 * Apply personalisation to <html>. Sync, idempotent — safe to call on every
 * settings change. Missing fields fall back to Quil defaults (robust against a
 * partially-populated settings row).
 */
export function applyPersonalization(settings: Partial<AppSettings> | null | undefined): void {
  const root = document.documentElement

  // No settings yet (store not bound, or still loading) and boot-theme.js already painted from the
  // mirror: leave that paint alone. Repainting the defaults here is what flashed forest green before
  // the writer's own ground arrived on every cold start.
  // A malformed mirror reads as none: boot-theme.js painted nothing from it, so paint here.
  if (settings || readMirror(themeStorage(), QUIL_THEME_KEY) === null) applyTheme(root, settings)

  applyRest(root, settings)
}

/** The inline paint for one built-in mode: the solved ground as --theme-* roles. */
function groundPaint(accent: string, theme: ThemeMode): { vars: Record<string, string>; bg: string; topbar: string } {
  // quil's profile recipe (THEME_PROFILES.quil.pickRecipe is this object; personalization.test.ts
  // pins that), imported directly so the eager bundle does not carry the five app profiles.
  const ground = solveGround({ base: accent, scheme: theme }, QUIL_HUE_RECIPE)
  const vars: Record<string, string> = {
    '--theme-bg': ground.bg,
    '--theme-surface-1': ground.surface1,
    '--theme-surface-2': ground.surface2,
    '--theme-sunk': ground.sunk,
  }
  // Light returns the pick as ink and hairline; the dark themes keep cream ink
  // and only OLED re-tints its keyline.
  const { text, line, topbar } = ground.extras
  if (text) vars['--theme-text'] = text
  if (line) vars['--theme-line'] = line
  return { vars, bg: ground.bg, topbar: topbar ?? ground.bg }
}

function applyTheme(root: HTMLElement, settings: Partial<AppSettings> | null | undefined): void {
  const stored: Theme = settings?.theme ?? DEFAULT_THEME
  const darkAs = settings?.autoDarkVariant ?? 'dark'
  const pref: ThemePref = stored === 'auto' ? 'system' : stored
  const ctx = { doc: document, win: window as { __themeLive?: boolean }, storage: themeStorage(), storageKey: QUIL_THEME_KEY }
  const mirror = { pref, systemDark: darkAs }

  // A pasted custom theme (per device) wins over the built-in Theme setting while it is active. Its
  // whole role map was derived and validated on paste; applying it needs no engine.
  const custom = activeCustomTheme()
  if (custom) {
    commitTheme(ctx, { theme: custom.scheme, customId: custom.id, vars: custom.vars, bg: custom.bg }, { ...mirror, pref: `custom:${custom.id}` })
    // --topbar is a quil-only extension: under a custom theme it keeps its built-in value.
    root.style.removeProperty('--topbar')
    return
  }

  const { theme } = resolveMode(pref, { systemDark: systemPrefersDark(), darkAs })
  const accent = settings?.accent ?? DEFAULT_ACCENT

  // THE PICK IS THE GROUND, not a fill (contract v3 §3, "the accent is the
  // ground"). The v3 reference has no accent-button role: it has a chosen ground
  // and a constant warm light, and the identity holds across green, oxblood,
  // teal and black because the relationship is fixed even when the hue is not.
  //
  // So --accent is NOT set here. It is the theme's lit element, defined in
  // tokens.quil.css: gold in dark and OLED, an amber in light, because gold
  // reaches only ~1.7:1 on the cream sheet. solveGround clamps the ladder against
  // EVERY ground of the active theme, not just --bg-base.
  //
  // The writes go to the canonical --theme-* roles, never the app names: --bg-base
  // and friends are aliases (`--bg-base: var(--theme-bg)`, theme.aliases.css), and
  // an inline app name would beat its alias (the contract's risk R2). --topbar is a
  // quil-only extension, so it keeps its own name.
  //
  // commitTheme (canonical themeApply.ts) writes data-theme, data-scheme, color-scheme and these
  // vars (removing any --theme-* it is not writing now), then the boot mirror, so the next cold start
  // paints this exact ground on frame one. For Auto it also mirrors the other scheme's ground (`alt`):
  // the system can flip between visits.
  const paint = groundPaint(accent, theme)
  let alt: Partial<Record<ThemeMode, { vars: Record<string, string>; bg: string }>> | undefined
  if (pref === 'system') {
    alt = {}
    for (const m of ['light', darkAs] as ThemeMode[]) {
      if (m !== theme) {
        const p = groundPaint(accent, m)
        alt[m] = { vars: p.vars, bg: p.bg }
      }
    }
  }
  commitTheme(ctx, { theme, vars: paint.vars, bg: paint.bg }, alt ? { ...mirror, alt } : mirror)
  root.style.setProperty('--topbar', paint.topbar)
}

function applyRest(root: HTMLElement, settings: Partial<AppSettings> | null | undefined): void {

  // Per-role font overrides.
  const fonts = settings?.fonts ?? {}
  for (const role of FONT_ROLES) {
    const varName = FONT_ROLE_VARS[role]
    const override = fonts[role]
    if (override) root.style.setProperty(varName, override)
    else root.style.removeProperty(varName)   // fall back to the token default
  }

  // Page & type comfort — editor-only tokens (only .ProseMirror reads --measure /
  // --read-line-height; only the indented selector reads data-paragraph-style).
  const measure = settings?.readingMeasure
  if (measure) root.style.setProperty('--measure', `${measure}ch`)
  else root.style.removeProperty('--measure')
  const lh = settings?.readingLineHeight
  if (lh) root.style.setProperty('--read-line-height', String(lh))
  else root.style.removeProperty('--read-line-height')
  root.setAttribute('data-paragraph-style', settings?.paragraphStyle ?? 'spaced')

  // Manual reduce-motion opt-in (F6, ported from Inkling): the OS
  // prefers-reduced-motion media query still applies on top of this — see the
  // `:root[data-reduce-motion='on'] *` override in global.css and
  // usePrefersReducedMotion.ts's JS-side check.
  if (settings?.reduceMotion) root.setAttribute('data-reduce-motion', 'on')
  else root.removeAttribute('data-reduce-motion')
}
